import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Modal,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import { Form, Formik } from "formik";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeader,
} from "../Common/styles/modal";
import { DrawerForm } from "../Common/styles/drawer";
import { MAX_OFFSET_IN_DAYS } from "../../utils/constants";

let schema = yup.object().shape({
  name: yup.string().trim().required("Study Phase Name is Required"),
  duration: yup
    .number()
    .min(0, "Offset cannot be negative")
    .max(
      MAX_OFFSET_IN_DAYS,
      `Offset cannot be greater than ${MAX_OFFSET_IN_DAYS} days`
    )
    .optional(),
});
type Props = {
  showModal: boolean;
  closeModal: () => void;
  data: any;
  studyId: any;
  refreshPage: () => void;
};

const AddVisitModal = ({
  showModal,
  closeModal,
  data,
  studyId,
  refreshPage,
}: Props) => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const submitHandler = async (values: any) => {
    try {
      setSubmitLoader(true);
      const body = {
        name: values?.name || "",
        duration:
          typeof values?.duration === "number" ? values?.duration : null,
        category: "visit",
      };
      if (data?.id) {
        await http.patch(`/study/${studyId}/study-phase/${data?.id}`, body);
      } else {
        await http.post(`/study/${studyId}/study-phase`, body);
      }
      toastMessage(
        "success",
        data?.id ? "Visit updated successfully" : "Visit created successfully"
      );
      closeModal();
      setSubmitLoader(false);
      refreshPage();
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  };

  return (
    <Modal open={showModal} onClose={closeModal}>
      <Box sx={ModalDrawerStyles}>
        <ModalHeader
          title={data?.id ? "Edit Visit" : "Add Visit"}
          onCloseClick={closeModal}
        />
        <Formik
          initialValues={{
            name: data?.name || "",
            duration: data?.duration ?? "",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ getFieldProps, errors, touched }) => (
            <Form style={DrawerForm}>
              <Box sx={{ display: "grid" }}>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="phase-name">
                    Visit Name <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    placeholder="Name"
                    id="phase-name"
                    {...getFieldProps("name")}
                    error={touched?.name && errors?.name ? true : false}
                    helperText={
                      touched?.name && errors?.name
                        ? (errors?.name as string)
                        : " "
                    }
                  />
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="duration">
                    Offset from Enrollment (in days)
                  </FormLabel>
                  <TextField
                    type="number"
                    placeholder="Offset"
                    id="duration"
                    {...getFieldProps("duration")}
                    error={touched?.duration && errors?.duration ? true : false}
                    helperText={
                      touched?.duration && errors?.duration
                        ? (errors?.duration as string)
                        : " "
                    }
                  />
                </FormControl>
              </Box>
              <Box sx={{ mt: "auto" }}>
                <Divider />
                <Box sx={ModalDrawerButtonStyles}>
                  {!submitLoader ? (
                    <>
                      <Button onClick={closeModal} variant="outlined">
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained">
                        {data?.id ? "Save" : "Add Visit"}
                      </Button>
                    </>
                  ) : (
                    <CircularProgress size={25} />
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddVisitModal;
